import type { Order } from '../../models/Order';
import type { OrderStatus } from '../../models/enums';
import { apiClient } from '../api/apiClient';
import { delay, readCollection, seedCollection } from './dataverseClient';
import { ordersSeed } from './mockData';

const ENTITY = 'cr_crm_order';

class OrderService {
  private ensureSeed(): void {
    seedCollection(ENTITY, ordersSeed);
  }

  async list(search = '', status?: OrderStatus): Promise<Order[]> {
    if (apiClient.isConfigured()) {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status !== undefined) params.set('status', String(status));
      const qs = params.toString();
      const res = await apiClient.get<{ totalCount: number; items: Order[] } | Order[]>(
        `/api/orders${qs ? `?${qs}` : ''}`
      );
      return Array.isArray(res) ? res : (res as { items: Order[] }).items ?? [];
    }
    this.ensureSeed();
    await delay();
    const term = search.toLowerCase();
    return readCollection<Order>(ENTITY)
      .filter((o) => (status !== undefined ? o.cr_crm_status === status : true))
      .filter((o) => !term || o.cr_crm_partnercenterorderid?.toLowerCase().includes(term))
      .sort((a, b) => (b.cr_crm_orderedat ?? '').localeCompare(a.cr_crm_orderedat ?? ''));
  }

  async getByClient(clientId: string): Promise<Order[]> {
    if (apiClient.isConfigured()) {
      const res = await apiClient.get<{ totalCount: number; items: Order[] } | Order[]>(
        `/api/orders?customerId=${clientId}`
      );
      return Array.isArray(res) ? res : (res as { items: Order[] }).items ?? [];
    }
    this.ensureSeed();
    await delay();
    return readCollection<Order>(ENTITY).filter((o) => o.cr_crm_clientid === clientId);
  }

  async getById(id: string): Promise<Order | null> {
    if (apiClient.isConfigured()) {
      return apiClient.get<Order>(`/api/orders/${id}`);
    }
    this.ensureSeed();
    await delay();
    return readCollection<Order>(ENTITY).find((o) => o.cr_crm_orderid === id) ?? null;
  }
}

export const orderService = new OrderService();
